import type { Message } from "./ai-client";
import type { DisplayMessage } from "./conversation-types";
import { debugLog } from "./debug";

export interface ConversationRecord {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: Message[];
  displayMessages: DisplayMessage[];
}

export const HISTORY_KEY = "aiAssistant.history.v1";
export const MAX_CONVERSATIONS = 50;
const TITLE_MAX = 60;

/** Reads and parses the stored array; corrupt/absent storage → []. Never throws. */
function readAll(): ConversationRecord[] {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(HISTORY_KEY);
  } catch (e) {
    debugLog("history:read-failed", e instanceof Error ? e.name : "unknown");
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ConversationRecord[]) : [];
  } catch {
    return [];
  }
}

/** Persists the array best-effort; a quota/disabled-storage failure is logged, not thrown. */
function writeAll(records: ConversationRecord[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(records));
  } catch (e) {
    debugLog("history:write-failed", e instanceof Error ? e.name : "unknown");
  }
}

/** All conversations, newest first by `updatedAt`. */
export function listConversations(): ConversationRecord[] {
  return readAll().sort((a, b) => b.updatedAt - a.updatedAt);
}

export function loadConversation(id: string): ConversationRecord | null {
  return readAll().find((r) => r.id === id) ?? null;
}

/** Upserts by id, then keeps only the newest MAX_CONVERSATIONS by updatedAt. */
export function saveConversation(record: ConversationRecord): void {
  const others = readAll().filter((r) => r.id !== record.id);
  const next = [record, ...others]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_CONVERSATIONS);
  writeAll(next);
}

export function deleteConversation(id: string): void {
  writeAll(readAll().filter((r) => r.id !== id));
}

export function newConversationId(): string {
  return crypto.randomUUID();
}

/** First user line as a title: trimmed, single-lined, truncated with an ellipsis. */
export function deriveTitle(firstUserText: string): string {
  const clean = firstUserText.trim().replace(/\s+/g, " ");
  if (clean.length === 0) return "New conversation";
  return clean.length > TITLE_MAX ? `${clean.slice(0, TITLE_MAX)}…` : clean;
}
